import axios from "axios";
import {
  getARVQueries,
  getCollectionQueries,
  getDashboardQueries,
  getDefaulterQueries,
  getDemandQueries,
  getDrCrQueries,
  getEditedPropertiesQueries,
  getMutationQueries,
  getNeftTransactionsQueries,
  getObjectionQueries,
  getOfflineTransactionsQueries,
  getOnlineTransactionsQueries,
  getPropertyQueries,
} from "./utils";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? process.env.API_URL || "https://api-citizen.arahas.com/saharanpur/"
    : "http://localhost:5055/saharanpur/";

axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.baseURL = API_BASE_URL;

/*=============================================
=            Handle API Error                =
=============================================*/

const handleApiError = (error) => {
  let errorMessage = "Something went wrong";
  let errorType = "error";

  if (error.response) {
    const { data, status: statusCode } = error.response;

    if (statusCode === 404) {
      errorMessage = data.message || "Resource not found";
      errorType = data.status;
    } else {
      errorMessage = data.message || "Server Error";
      errorType = data.status;
    }
  } else if (error.request) {
    errorMessage = "No response received from the server";
  } else {
    errorMessage = error.message || "Request failed";
  }
  throw new Error(`${errorType}: ${errorMessage}`);
};

/*=====  End of handling API error  ======*/

export const API = {
  /*=============================================
=            Zones APIs                       =
=============================================*/

  fetchFilteredZones: async () => {
    try {
      const response = await axios.get(`zones`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllZones: async () => {
    try {
      const response = await axios.get(`zones/all`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addZone: async (values) => {
    try {
      const response = await axios.post(`zones/add`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Zone APIs  ======*/

  /*=============================================
=            Wards APIs                      =
=============================================*/

  fetchAllWards: async (selectedZone) => {
    try {
      const response = await axios.get(
        `wards/all${selectedZone ? `?zone=${selectedZone}` : ""}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllWardsByZone: async (selectedZone) => {
    try {
      const response = await axios.get(
        `wards${selectedZone ? `?zone=${selectedZone}` : ""}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  editWard: async (wardId, values) => {
    try {
      const response = await axios.patch(`wards/edit/${wardId}`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addWard: async (values) => {
    try {
      const response = await axios.post(`wards/add`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Wards APIs  ======*/

  /*=============================================
=            Mohallas APIs                       =
=============================================*/

  fetchAllMohallas: async () => {
    try {
      const response = await axios.get(`mohallas`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  editMohalla: async (mohallaId, formData) => {
    try {
      const response = await axios.patch(
        `mohallas/edit/${mohallaId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log(response.data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addMohalla: async (values) => {
    try {
      const response = await axios.post(`mohallas/add`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllMohallasByWardsAndZone: async (selectedZone, selectedWard) => {
    try {
      const response = await axios.get(
        `mohallas?zone=${selectedZone}&ward=${selectedWard}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchMohallasByWard: async (selectedWard) => {
    try {
      const response = await axios.get(`mohallas?ward=${selectedWard}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  /*=====  End of Mohallas APIs  ======*/

  /*=============================================
=            Roles APIs                       =
=============================================*/

  fetchAllRoles: async () => {
    try {
      const response = await axios.get(`roles/all`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  editRole: async (roleId, values) => {
    try {
      const response = await axios.patch(`roles/edit/${roleId}`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addRole: async (values) => {
    try {
      const response = await axios.post(`roles/add/`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Roles APIs  ======*/

  fetchBanks: async () => {
    try {
      const response = await axios.get(`banks`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchCategories: async () => {
    try {
      const response = await axios.get(`categories`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addCategory: async (values) => {
    try {
      const response = await axios.post(`categories`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  updateCategory: async (categoryID, values) => {
    try {
      const response = await axios.patch(
        `categories/categoryID=${categoryID}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  deleteCategory: async (categoryID) => {
    try {
      const response = await axios.delete(
        `categories/categoryID=${categoryID}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllFilteredProperties: async (values) => {
    try {
      const response = await axios.post(`properties/all`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  generateBulkNotices: async (formData) => {
    try {
      const response = await axios.post(
        `properties/generateBulkNotice`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchPropertyByID: async (propertyUniqueId) => {
    try {
      const response = await axios.get(
        `properties?propertyUniqueId=${propertyUniqueId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  searchPropertyDetails: async (query, values) => {
    try {
      let queryConditions;

      if (query.page && query.pageSize) {
        queryConditions = `?page=${query.page}&limit=${query.pageSize}`;
      }

      const response = await axios.post(
        `properties/search${queryConditions ? queryConditions : ""}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  messageSearchPropertyDetails: async (values) => {
    try {
      let queryConditions;

      const response = await axios.post(
        `properties/message/search${queryConditions ? queryConditions : ""}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchPaginatedProperties: async (query, values) => {
    try {
      let queryConditions = "";

      if (query.page && query.pageSize) {
        queryConditions = `?page=${query.page}&limit=${query.pageSize}`;
      }

      const response = await axios.post(
        `properties/paginated${queryConditions}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportPropertyDetails: async (values) => {
    try {
      const queryConditions = getPropertyQueries(values);
      const response = await axios.get(`properties/export${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addProperty: async (formData) => {
    try {
      const response = await axios.post(`properties/add`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  editPropertyDetails: async (propertyUniqueId, formData) => {
    try {
      const response = await axios.patch(
        `properties/edit/${propertyUniqueId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchDebitCreditNote: async (paginationQuery, values) => {
    try {
      const queryConditions = getDrCrQueries(paginationQuery, values);

      const response = await axios.get(`/drcrNotes${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportDebitCreditNote: async (values) => {
    try {
      let queryConditions = getDrCrQueries({}, values);

      const response = await axios.get(`/drcrNotes/export${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  correctArrear: async (propertyUniqueId, formData) => {
    try {
      const response = await axios.post(
        `/properties/correctArrear/${propertyUniqueId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  editPropertyFloor: async (propertyUniqueId, values) => {
    try {
      const response = await axios.put(
        `properties/premises/${propertyUniqueId}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addFloorToProperty: async (propertyUniqueId, values) => {
    try {
      const response = await axios.post(
        `properties/premises/${propertyUniqueId}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchLedger: async (values) => {
    try {
      const response = await axios.post(`properties/ledger`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  generateDemandBill: async (propertyRefId) => {
    try {
      const response = await axios.get(
        `taxDemands/demandBillDetails?propertyId=${propertyRefId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  generateBulkDemandBill: async (values) => {
    try {
      const response = await axios.post(`taxDemands/bulkDemandBill`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  generateExcelDemandBill: async (formData) => {
    try {
      const response = await axios.post(
        `taxDemands/bulkDemandBillByExcel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getPaymentDetails: async (propertyRefId, mode, year) => {
    try {
      const response = await axios.get(
        `taxDemands/paymentDetails?propertyId=${propertyRefId}&mode=${mode}&year=${year}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=============================================
=            Objection APIs                       =
=============================================*/
  fetchAllObjections: async (values) => {
    try {
      let queryConditions = getObjectionQueries(values);

      const response = await axios.get(`objection${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchResolvedObjections: async (values) => {
    try {
      let queryConditions = getObjectionQueries(values);

      const response = await axios.get(`objection/resolved${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportAllObjections: async (values) => {
    try {
      let queryConditions = getObjectionQueries(values);

      const response = await axios.get(`objection/export${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchEditedProperties: async (values) => {
    try {
      let queryConditions = getEditedPropertiesQueries(values);

      const response = await axios.get(`properties/edited${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportEditedProperties: async (values) => {
    try {
      let queryConditions = getEditedPropertiesQueries(values);

      const response = await axios.get(
        `properties/edited/export${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchObjectionsSummary: async (values) => {
    try {
      const queryConditions = getObjectionQueries(values);

      const response = await axios.get(`objection/summary${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchObjectionStatus: async (query, values) => {
    try {
      let queryConditions;

      if (query.page && query.pageSize) {
        queryConditions = `?page=${query.page}&limit=${query.pageSize}`;
      }

      const response = await axios.post(
        `/objection/status${queryConditions}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  uploadObjection: async (formData) => {
    try {
      const response = await axios.post(`objection/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  rejectObjection: async (objectionId, values) => {
    try {
      const response = await axios.patch(
        `objection/reject/${objectionId}`,
        values
      );

      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Objection APIs  ======*/

  loginUploadUser: async (values) => {
    try {
      const response = await axios.post(`operators/login`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  loginOfficial: async (values) => {
    try {
      const response = await axios.post(`officials/login`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllOperators: async () => {
    try {
      const response = await axios.get(`operators/all`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllRIs: async () => {
    try {
      const response = await axios.get(`officials/allRI`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addOperator: async (values) => {
    try {
      const response = await axios.post(`operators`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllOfficials: async () => {
    try {
      const response = await axios.get(`officials/all`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addOfficial: async (values) => {
    try {
      const response = await axios.post(`officials`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  editOfficial: async (values) => {
    try {
      const response = await axios.patch(`officials`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  deleteOfficial: async (userId) => {
    try {
      const response = await axios.delete(`officials/delete/${userId}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchUploadUser: async (email) => {
    try {
      const response = await axios.get(`operators?email=${email}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchRoles: async () => {
    try {
      const response = await axios.get(`roles`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchOfficial: async (email) => {
    try {
      const response = await axios.get(`officials?email=${email}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=============================================
=            Discount APIs                       =
=============================================*/

  fetchDiscount: async () => {
    try {
      const response = await axios.get(`discounts`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addDiscount: async (values) => {
    try {
      const response = await axios.post(`discounts/add`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  editDiscountById: async (discountId, values) => {
    try {
      const response = await axios.patch(
        `discounts/edit/${discountId}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchDiscountsForPosting: async () => {
    try {
      const response = await axios.get(`discounts/posting`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Discount APIs  ======*/

  editJalkalProperty: async (values) => {
    try {
      const response = await axios.post(`jalkal`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=============================================
=            Edit APIs                       =
=============================================*/
  fetchAllPendingEdits: async (values) => {
    try {
      const response = await axios.post(`jalkal/pending`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllApprovedEdits: async (values) => {
    try {
      const response = await axios.post(`jalkal/approved`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllRejectedEdits: async (values) => {
    try {
      const response = await axios.post(`jalkal/rejected`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllArchivedEdits: async () => {
    try {
      const response = await axios.get(`jalkal/archived`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  approveEdit: async (editId, userId, remarks) => {
    try {
      const response = await axios.patch(`jalkal/approve/${editId}`, {
        userId: userId,
        comment: remarks,
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  rejectEdit: async (editId, userId, remarks) => {
    try {
      const response = await axios.patch(`jalkal/reject/${editId}`, {
        userId: userId,
        comment: remarks,
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Edit APIs  ======*/

  changeOfficialsPassword: async (values) => {
    try {
      const response = await axios.post(`officials/changePassword`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  resetOfficialsPassword: async (values) => {
    try {
      const response = await axios.post(`officials/resetPassword`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  changeUploadUserPassword: async (values) => {
    try {
      const response = await axios.post(`operators/changePassword`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  resetOperatorsPassword: async (values) => {
    try {
      const response = await axios.post(`operators/resetPassword`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  resolveObjection: async (propertyRefUniqueId, formData) => {
    try {
      const response = await axios.patch(
        `properties/resolveObjection?propertyRefUniqueId=${propertyRefUniqueId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  editProperty: async (propertyRefUniqueId, dataToUpdate) => {
    try {
      const response = await axios.patch(
        `properties/editProperty?propertyRefUniqueId=${propertyRefUniqueId}`,
        dataToUpdate
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  splitProperty: async (propertyUniqueId, splitData) => {
    try {
      const response = await axios.patch(
        `properties/splitProperty?propertyUniqueId=${propertyUniqueId}`,
        splitData
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  calculateTax: async (propertyRefUniqueId) => {
    try {
      const response = await axios.get(
        `properties/calculateTax?propertyRefUniqueId=${propertyRefUniqueId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchTaxCalculations: async (propertyRefUniqueId) => {
    try {
      const response = await axios.get(
        `properties/taxCalculations?propertyRefUniqueId=${propertyRefUniqueId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  calculateMohallaPropertiesTax: async (mohallaId) => {
    try {
      const response = await axios.patch(
        `properties/calculateMohallaTax?mohallaId=${mohallaId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchCityDetails: async () => {
    try {
      const response = await axios.get(`cities`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addSelfAssessedProperty: async (values) => {
    try {
      const response = await axios.post(`selfAssessment`, values, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchOldTransactions: async (values) => {
    try {
      let queryConditions = "";

      if (values.oldPropertyTaxId) {
        queryConditions += `${queryConditions ? "&" : "?"}oldPropertyTaxId=${
          values.oldPropertyTaxId
        }`;
      }

      if (values.zoneId) {
        queryConditions += `${queryConditions ? "&" : "?"}zoneId=${
          values.zoneId
        }`;
      }

      if (values.wardId) {
        queryConditions += `${queryConditions ? "&" : "?"}wardId=${
          values.wardId
        }`;
      }

      if (values.mohallaId) {
        queryConditions += `${queryConditions ? "&" : "?"}mohallaId=${
          values.mohallaId
        }`;
      }

      if (values.hasPaid) {
        queryConditions += `${queryConditions ? "&" : "?"}hasPaid=${
          values.hasPaid
        }`;
      }

      if (values.page && values.pageSize) {
        queryConditions += `${queryConditions ? "&" : "?"}page=${
          values.page
        }&limit=${values.pageSize}`;
      }

      if (values?.dates && values?.dates.length === 2) {
        queryConditions += `${queryConditions ? "&" : "?"}startDate=${new Date(
          values?.dates[0]
        ).toLocaleDateString()}&endDate=${new Date(
          values?.dates[1]
        ).toLocaleDateString()}`;
      }

      const response = await axios.get(`oldPayments${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportOldTransactions: async (values) => {
    try {
      let queryConditions = "";

      if (values.oldPropertyTaxId) {
        queryConditions += `${queryConditions ? "&" : "?"}oldPropertyTaxId=${
          values.oldPropertyTaxId
        }`;
      }

      if (values.zoneId) {
        queryConditions += `${queryConditions ? "&" : "?"}zoneId=${
          values.zoneId
        }`;
      }

      if (values.wardId) {
        queryConditions += `${queryConditions ? "&" : "?"}wardId=${
          values.wardId
        }`;
      }

      if (values.mohallaId) {
        queryConditions += `${queryConditions ? "&" : "?"}mohallaId=${
          values.mohallaId
        }`;
      }

      if (values.hasPaid) {
        queryConditions += `${queryConditions ? "&" : "?"}hasPaid=${
          values.hasPaid
        }`;
      }

      if (values?.dates && values?.dates.length === 2) {
        queryConditions += `${queryConditions ? "&" : "?"}startDate=${new Date(
          values?.dates[0]
        ).toLocaleDateString()}&endDate=${new Date(
          values?.dates[1]
        ).toLocaleDateString()}`;
      }

      const response = await axios.get(`oldPayments/export${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllTransactions: async (values) => {
    try {
      let queryConditions = getOfflineTransactionsQueries(values);

      const response = await axios.get(`transactions${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllPOSTransactions: async (values) => {
    try {
      let queryConditions = getOfflineTransactionsQueries(values);

      const response = await axios.get(`transactions/pos${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchArchivedTransactions: async () => {
    try {
      const response = await axios.get(`transactions/archived`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addTransaction: async (values) => {
    try {
      const response = await axios.post(`transactions`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  approveTransaction: async (transactionId) => {
    try {
      const response = await axios.patch(
        `transactions/approve/${transactionId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  rejectTransaction: async (transactionId, remarks) => {
    try {
      const response = await axios.post(
        `transactions/reject/${transactionId}`,
        remarks
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllTransactionsAmount: async (values) => {
    try {
      let queryConditions = getOfflineTransactionsQueries(values);

      const response = await axios.get(
        `transactions/summary${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchPOSTransactionsSummary: async (values) => {
    try {
      let queryConditions = getOfflineTransactionsQueries(values);

      const response = await axios.get(
        `transactions/pos/summary${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchNEFTTransactions: async (values) => {
    try {
      let queryConditions = getNeftTransactionsQueries(values);

      const response = await axios.get(`neftPayments/all${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportNEFTTransactions: async (values) => {
    try {
      let queryConditions = getNeftTransactionsQueries(values);

      const response = await axios.get(`neftPayments/export${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchNEFTTransactionsSummary: async (values) => {
    try {
      let queryConditions = getNeftTransactionsQueries(values);

      const response = await axios.get(
        `neftPayments/summary${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportNEFTTransactionSummary: async (values) => {
    try {
      let queryConditions = getNeftTransactionsQueries(values);

      const response = await axios.get(
        `neftPayments/summary/export${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchApplicationsCitizen: async () => {
    try {
      const response = await axios.get(`selfAssessment`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchApplicationsNigam: async () => {
    try {
      const response = await axios.get(`selfAssessment/all`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  initiateOfflinePayment: async (values) => {
    try {
      const response = await axios.post(
        `transactions/initiate_payment`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchDiscountAndPayableAmount: async (values) => {
    try {
      const response = await axios.post(`payments/calculateDiscounts`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  initiatePayment: async (values) => {
    try {
      const response = await axios.post(`payments/initiate_payment`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  completeNEFTPayment: async (formData) => {
    try {
      const response = await axios.post(`neftPayments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  sendTaxCollectionMessage: async (values) => {
    try {
      const response = await axios.post(`payments/easyCollect`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  sendBulkTaxCollectionMessage: async (values, type) => {
    try {
      const response = await axios.post(
        `payments/easyCollectBulk?type=${type}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchOnlineTransaction: async (values) => {
    try {
      const response = await axios.post(`snn-payments/transaction`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllOnlineTransactions: async (values) => {
    try {
      let queryConditions = getOnlineTransactionsQueries(values);

      const response = await axios.get(`snn-payments/all${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportOnlineTransactions: async (values) => {
    try {
      let queryConditions = getOnlineTransactionsQueries(values);

      const response = await axios.get(`snn-payments/export${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportOnlineTxnSummary: async (values) => {
    try {
      let queryConditions = getOnlineTransactionsQueries(values);
      const response = await axios.get(
        `snn-payments/summary/export${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportOfflineTransactions: async (values) => {
    try {
      let queryConditions = getOfflineTransactionsQueries(values);

      const response = await axios.get(`transactions/export${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportPOSTransactions: async (values) => {
    try {
      let queryConditions = getOfflineTransactionsQueries(values);

      const response = await axios.get(
        `transactions/pos/export${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchTransactionsSummary: async (values) => {
    try {
      let queryConditions = getOfflineTransactionsQueries(values);

      const response = await axios.get(
        `snn-payments/summary${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchTransactionsByDate: async (values) => {
    try {
      const response = await axios.post(`payments/transaction_date`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchPayoutsByDate: async (values) => {
    try {
      const response = await axios.post(`payments/payout`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchPostingTransactions: async (values) => {
    try {
      const response = await axios.get(`transactions/posting-summary`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=============================================
=            Tax Demand APIs            =
=============================================*/

  fetchTaxSummary: async (query) => {
    try {
      const demandQueries = getDemandQueries(query);

      const response = await axios.get(`properties/taxSummary${demandQueries}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchTotalDemandSummary: async (query) => {
    try {
      const demandQueries = getDemandQueries(query);

      const response = await axios.get(
        `taxDemands/totalSummary${demandQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchSplitDemandSummary: async (query) => {
    try {
      const demandQueries = getDemandQueries(query);

      const response = await axios.get(
        `taxDemands/splitSummary${demandQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchZoneWiseDemandSummary: async (query) => {
    try {
      const demandQueries = getDemandQueries(query);

      const response = await axios.get(
        `taxDemands/zoneWiseSummary${demandQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchZoneAndUsageWiseDemandSummary: async (query) => {
    try {
      const demandQueries = getDemandQueries(query);

      const response = await axios.get(
        `taxDemands/zoneAndUsageWiseSummary${demandQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchDefaultersList: async (paginationQuery, values) => {
    try {
      const queryConditions = getDefaulterQueries(paginationQuery, values);

      const response = await axios.get(
        `taxDemands/defaultersList${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportDefaultersList: async (values) => {
    try {
      const queryConditions = getDefaulterQueries({}, values);

      const response = await axios.get(
        `taxDemands/defaultersList/export${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchARVList: async (paginationQuery, values) => {
    try {
      const queryConditions = getARVQueries(paginationQuery, values);
      const response = await axios.get(`properties/arvList${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportARVList: async (values) => {
    try {
      const queryConditions = getARVQueries({}, values);

      const response = await axios.get(
        `properties/arvList/export${queryConditions}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Tax Demand APIs  ======*/

  /*=============================================
=            Tax Collection APIs            =
=============================================*/

  fetchTotalCollection: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/summary${collectionQueries}`
      );

      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchCollectionList: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/collectionList${collectionQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchTypeWiseCollection: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/typeWise${collectionQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchMonthWiseCollection: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/monthWise${collectionQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchMonthlyCollection: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/monthly${collectionQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchZoneWiseCollection: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/zoneWise${collectionQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchWardWiseCollection: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/wardWise${collectionQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchMohallaWiseCollection: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/mohallaWise${collectionQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchRIWiseCollection: async (query) => {
    try {
      const collectionQueries = getCollectionQueries(query);

      const response = await axios.get(
        `taxCollections/riWise${collectionQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Tax Collection APIs  ======*/

  updateOwnerDetails: async (values) => {
    try {
      const response = await axios.patch(
        `properties/updateOwnerDetails`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  updateBuildingImages: async (selectedProperty, formData) => {
    try {
      const response = await axios.patch(
        `properties/updateBuildingImages?propertyUniqueId=${selectedProperty.propertyUniqueId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  createMutationRequest: async (formData) => {
    try {
      const response = await axios.post(`mutations`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchMutations: async () => {
    try {
      const response = await axios.get(`mutations`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAllMutations: async (values) => {
    try {
      let queryConditions = getMutationQueries(values);

      const response = await axios.get(`mutations/all${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  approveMutation: async (mutationId, userId, remarks) => {
    try {
      if (!mutationId) {
        throw new Error("Mutation Id is required");
      }
      const response = await axios.patch(`mutations/approve/${mutationId}`, {
        userId,
        remarks,
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  rejectMutation: async (mutationId, values) => {
    try {
      const response = await axios.patch(
        `mutations/reject/${mutationId}`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  removeMatchingDetails: async (propertyId) => {
    try {
      const response = await axios.patch(
        `properties/removeMatchingDetails/${propertyId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchPTINDetails: async (ptinNo) => {
    try {
      const response = await axios.get(`oldProperties/byPTIN?ptin=${ptinNo}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  insertTransactionToDB: async (values) => {
    try {
      const response = await axios.post(`payments/syncPayment`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchOldProperties: async (query, values) => {
    try {
      let queryConditions = "";

      if (query.page && query.pageSize) {
        queryConditions = `?page=${query.page}&limit=${query.pageSize}`;
      }

      if (values.oldPropertyTaxId) {
        queryConditions += `&oldPropertyTaxId=${values.oldPropertyTaxId}`;
      }

      const response = await axios.get(`/oldProperties${queryConditions}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  addOldProperty: async (values) => {
    try {
      const response = await axios.post(`oldProperties/add`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  propertyCount: async () => {
    try {
      const response = await axios.get(`properties/count`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  replaceTaxDemand: async (propertyUniqueId) => {
    try {
      const response = await axios.post(
        `taxDemands/replace?propertyUniqueId=${propertyUniqueId}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=============================================
=            Dashboard Report APIs                       =
=============================================*/

  fetchTypeWiseCount: async (values) => {
    try {
      let demandQueries = getDashboardQueries({}, values);

      if (values.matchingStatus && values.matchingStatus !== "") {
        demandQueries += `${demandQueries ? "&" : "?"}matchingStatus=${
          values.matchingStatus
        }`;
      }

      const response = await axios.get(
        `properties/propertyWise${demandQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchARVRangeDetails: async (range, paginationQuery, values) => {
    try {
      const demandQueries = getDashboardQueries(paginationQuery, values);
      const queryString = `range=${range}&${demandQueries}`;

      const response = await axios.get(`properties/arvWise?${queryString}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportARVRangeDetails: async (range, values) => {
    try {
      const demandQueries = getDashboardQueries({}, values);
      const queryString = `range=${range}&${demandQueries}`;

      const response = await axios.get(
        `properties/arvWise/export?${queryString}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchCategoryWiseDetails: async (category, paginationQuery, values) => {
    try {
      const demandQueries = getDashboardQueries(paginationQuery, values);
      const queryString = `category=${category}&${demandQueries}`;

      const response = await axios.get(
        `properties/categoryWise?${queryString}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  exportCategoryWiseDetails: async (category, values) => {
    try {
      const demandQueries = getDashboardQueries({}, values);
      const queryString = `category=${category}&${demandQueries}`;

      const response = await axios.get(
        `properties/categoryWise/export?${queryString}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchViewDetails: async (values) => {
    try {
      const demandQueries = getDashboardQueries({}, values);

      const response = await axios.get(
        `properties/viewDetails${demandQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchPropertiesSummary: async (values) => {
    try {
      let demandQueries = getDashboardQueries({}, values);

      if (values.matchingStatus && values.matchingStatus !== "") {
        demandQueries += `${demandQueries ? "&" : "?"}matchingStatus=${
          values.matchingStatus
        }`;
      }
      const response = await axios.get(
        `properties/summaryProperties${demandQueries}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Dashboard Report APIs  ======*/

  fetchGISReportData: async (reportType) => {
    try {
      const response = await axios.get(
        `taxDemands/getGISReport?reportType=${reportType}`
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=============================================
=            GIS Module APIs                       =
=============================================*/

  fetchGeoJson: async () => {
    try {
      const response = await axios.get(`mapSession/`);
      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of GIS Module APIs  ======*/

  /*=============================================
  =            Logs APIs                       =
  =============================================*/

  fetchLogs: async (queryParams) => {
    try {
      const response = await axios.get(
        `logs${queryParams ? `?${queryParams.toString()}` : ""}`
      );

      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAdminEditLogs: async (queryParams) => {
    try {
      const response = await axios.get(
        `logs/adminEdits${queryParams ? `?${queryParams.toString()}` : ""}`
      );

      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchMatchingRemovedLogs: async (queryParams) => {
    try {
      const response = await axios.get(
        `logs/matchingRemoved${queryParams ? `?${queryParams.toString()}` : ""}`
      );

      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchDeletedPropertiesLogs: async (queryParams) => {
    try {
      const response = await axios.get(
        `logs/deletedProperties${
          queryParams ? `?${queryParams.toString()}` : ""
        }`
      );

      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchAddedPropertiesLogs: async (queryParams) => {
    try {
      const response = await axios.get(
        `logs/addedProperties${queryParams ? `?${queryParams.toString()}` : ""}`
      );

      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchPaymentShiftLogs: async (queryParams) => {
    try {
      const response = await axios.get(
        `logs/paymentShift${queryParams ? `?${queryParams.toString()}` : ""}`
      );

      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  fetchSplitLogs: async (queryParams) => {
    try {
      const response = await axios.get(
        `logs/splits${queryParams ? `?${queryParams.toString()}` : ""}`
      );

      return response;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=============================================
=            Admin Utility APIs                       =
=============================================*/

  shiftPayment: async (values) => {
    try {
      const response = await axios.patch(`transactions/shiftPayment`, values);

      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  cancelPayment: async (values) => {
    try {
      const response = await axios.patch(`transactions/cancelPayment`, values);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  bulkMatch: async (formData) => {
    try {
      const response = await axios.post(
        `properties/updatePTINFromExcel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  bulkUpdateNonTaxable: async (formData) => {
    try {
      const response = await axios.patch(
        `properties/updateFromExcel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  bulkUpdateRoadWidth: async (formData) => {
    try {
      const response = await axios.patch(
        `properties/updateRoadWidth`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  bulkMohallaUpdate: async (formData) => {
    try {
      const response = await axios.patch(
        `properties/updateBulkMohallaExcel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.message;
    } catch (error) {
      handleApiError(error);
    }
  },

  bulkUpdatePropertyAddress: async (formData) => {
    try {
      const response = await axios.post(
        `properties/updatePropertyAddress`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.message;
    } catch (error) {
      handleApiError(error);
    }
  },

  splitFromExcel: async (propertyId, formData) => {
    try {
      const response = await axios.patch(
        `properties/splitFromExcel?propertyUniqueId=${propertyId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  deleteProperty: async (values) => {
    try {
      const response = await axios.post(`properties/delete`, values, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  updateTaxDemandFromTxn: async (values) => {
    try {
      const response = await axios.get(
        `taxDemands/fetchUpdatedTotals?propertyUniqueId=${values.propertyUniqueId}`
      );

      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  pushToENagarSewa: async () => {
    try {
      const response = await axios.get(`taxCollections/pushToENagarSewa`);

      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  /*=====  End of Admin Utility Module APIs  ======*/

  /*=============================================
=            Razorpay Integration APIs             =
=============================================*/

  initiateRazorpayPayment: async (values) => {
    try {
      const response = await axios.post(
        `snn-payments/initiate_payment`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  getCheckoutOptions: async (values) => {
    try {
      const response = await axios.post(
        `snn-payments/getCheckoutOptions`,
        values
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },
  /*=====  End of Razorpay Integration APIs  ======*/
};
